const API_BASE = '/api/v1/wearable';

// ============================================
// TYPES
// ============================================

export interface DailyMetric {
  id: number;
  userId: string;
  metricDate: string;
  steps: number | null;
  caloriesActive: number | null;
  caloriesTotal: number | null;
  distance: number | null;
  activeMinutes: number | null;
  floorsClimbed: number | null;
  readinessScore: number | null;
  source: string;
  createdAt: string;
}

export interface SleepData {
  id: number;
  userId: string;
  sleepDate: string;
  bedtime: string | null;
  wakeTime: string | null;
  totalSleepMinutes: number | null;
  deepSleepMinutes: number | null;
  lightSleepMinutes: number | null;
  remSleepMinutes: number | null;
  awakeMinutes: number | null;
  sleepEfficiency: number | null;
  sleepScore: number | null;
  avgHeartRate: number | null;
  restingHeartRate: number | null;
  hrv: number | null;
  source: string;
  createdAt: string;
}

export interface Workout {
  id: number;
  userId: string;
  workoutDate: string;
  workoutType: string;
  duration: number;
  distance: number | null;
  calories: number | null;
  avgHeartRate: number | null;
  maxHeartRate: number | null;
  elevationGain: number | null;
  source: string;
  externalId: string | null;
  notes: string | null;
  createdAt: string;
}

export interface HeartRateData {
  id: number | string;
  userId: string;
  timestamp: string;
  heartRate: number;
  minHeartRate?: number;  // Available when data is aggregated
  maxHeartRate?: number;  // Available when data is aggregated
  source: string;
  activityType: string | null;
}

export interface HealthGoal {
  id: number;
  userId: string;
  goalType: string;
  title: string;
  description: string | null;
  targetValue: number;
  currentValue: number;
  unit: string;
  startDate: string;
  targetDate: string | null;
  progressPercentage: number;
  milestones: unknown[] | null;
  isActive: boolean;
  isAchieved: boolean;
  achievedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WearableSummary {
  period: { days: number; startDate: string };
  steps: {
    average: number;
    latest: number;
    latestDate: string | undefined;
  };
  sleep: {
    averageMinutes: number;
    latestMinutes: number;
    latestDate: string | undefined;
    avgHRV: number | undefined;
  };
  workouts: {
    total: number;
    totalMinutes: number;
  };
  sources: string[];
}

export interface DateRangeParams {
  startDate?: string;
  endDate?: string;
  source?: string;
}

export interface ImportResult {
  source: string;
  dailyMetrics: number;
  sleepRecords: number;
  heartRateRecords: number;
  workouts: number;
}

export interface OuraStatus {
  connected: boolean;
  email?: string;
}

export interface StravaStatus {
  connected: boolean;
  athlete?: {
    id: number;
    firstname: string;
    lastname: string;
    profile: string;
  };
  expiresAt?: string;
}

export interface GarminStatus {
  connected: boolean;
  displayName?: string;
  username?: string;
  lastSync?: string;
  needsReauth?: boolean;
}

export interface BodyBatteryData {
  id: number;
  userId: string;
  date: string;
  charged: number;
  drained: number;
  startLevel: number;
  endLevel: number;
  source: string;
  createdAt: string;
}

export interface StressData {
  id: number;
  userId: string;
  date: string;
  averageStress: number;
  maxStress: number;
  stressQualifier: string | null;
  restMinutes: number | null;
  lowMinutes: number | null;
  mediumMinutes: number | null;
  highMinutes: number | null;
  source: string;
  createdAt: string;
}

export interface RespirationData {
  id: number;
  userId: string;
  date: string;
  avgBreathingRate: number;
  minBreathingRate: number | null;
  maxBreathingRate: number | null;
  source: string;
  createdAt: string;
}

// ============================================
// SUMMARY & DASHBOARD
// ============================================

export async function getWearableSummary(days = 7): Promise<{ success: boolean; summary: WearableSummary }> {
  const response = await fetch(`${API_BASE}/summary?days=${days}`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nel caricamento del riepilogo');
  return response.json();
}

// ============================================
// DAILY METRICS
// ============================================

export async function getDailyMetrics(params?: DateRangeParams): Promise<{ success: boolean; metrics: DailyMetric[] }> {
  const searchParams = new URLSearchParams();
  if (params?.startDate) searchParams.set('startDate', params.startDate);
  if (params?.endDate) searchParams.set('endDate', params.endDate);
  if (params?.source) searchParams.set('source', params.source);

  const query = searchParams.toString();
  const response = await fetch(`${API_BASE}/daily-metrics${query ? `?${query}` : ''}`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nel caricamento delle metriche giornaliere');
  return response.json();
}

export async function createDailyMetric(data: {
  date: string;
  steps?: number;
  caloriesActive?: number;
  caloriesTotal?: number;
  distance?: number;
  activeMinutes?: number;
  floorsClimbed?: number;
}): Promise<{ success: boolean; metric: DailyMetric }> {
  const response = await fetch(`${API_BASE}/daily-metrics`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Errore nel salvataggio');
  return response.json();
}

// ============================================
// SLEEP DATA
// ============================================

export async function getSleepData(params?: DateRangeParams): Promise<{ success: boolean; sleep: SleepData[] }> {
  const searchParams = new URLSearchParams();
  if (params?.startDate) searchParams.set('startDate', params.startDate);
  if (params?.endDate) searchParams.set('endDate', params.endDate);
  if (params?.source) searchParams.set('source', params.source);

  const query = searchParams.toString();
  const response = await fetch(`${API_BASE}/sleep${query ? `?${query}` : ''}`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nel caricamento dei dati del sonno');
  return response.json();
}

export async function createSleepRecord(data: {
  date: string;
  bedtime?: string;
  wakeTime?: string;
  totalSleepMinutes: number;
  deepSleepMinutes?: number;
  lightSleepMinutes?: number;
  remSleepMinutes?: number;
  awakeMinutes?: number;
  sleepEfficiency?: number;
  sleepScore?: number;
}): Promise<{ success: boolean; sleep: SleepData }> {
  const response = await fetch(`${API_BASE}/sleep`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Errore nel salvataggio');
  return response.json();
}

// ============================================
// WORKOUTS
// ============================================

export async function getWorkouts(params?: DateRangeParams & { type?: string }): Promise<{ success: boolean; workouts: Workout[] }> {
  const searchParams = new URLSearchParams();
  if (params?.startDate) searchParams.set('startDate', params.startDate);
  if (params?.endDate) searchParams.set('endDate', params.endDate);
  if (params?.source) searchParams.set('source', params.source);
  if (params?.type) searchParams.set('type', params.type);

  const query = searchParams.toString();
  const response = await fetch(`${API_BASE}/workouts${query ? `?${query}` : ''}`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nel caricamento degli allenamenti');
  return response.json();
}

export async function createWorkout(data: {
  date: string;
  workoutType: string;
  duration: number;
  distance?: number;
  calories?: number;
  avgHeartRate?: number;
  maxHeartRate?: number;
  elevationGain?: number;
  notes?: string;
}): Promise<{ success: boolean; workout: Workout }> {
  const response = await fetch(`${API_BASE}/workouts`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Errore nel salvataggio');
  return response.json();
}

export async function deleteWorkout(id: number): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/workouts/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nell\'eliminazione');
  return response.json();
}

// ============================================
// HEART RATE
// ============================================

export async function getHeartRateData(params?: DateRangeParams): Promise<{ success: boolean; heartRate: HeartRateData[] }> {
  const searchParams = new URLSearchParams();
  if (params?.startDate) searchParams.set('startDate', params.startDate);
  if (params?.endDate) searchParams.set('endDate', params.endDate);
  if (params?.source) searchParams.set('source', params.source);

  const query = searchParams.toString();
  const response = await fetch(`${API_BASE}/heart-rate${query ? `?${query}` : ''}`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nel caricamento dei dati');
  return response.json();
}

// ============================================
// CSV IMPORT
// ============================================

export async function importCSV(file: File): Promise<{ success: boolean; imported: ImportResult }> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE}/import-csv`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Errore durante l\'import');
  }
  return response.json();
}

// ============================================
// DELETE BY SOURCE
// ============================================

export async function deleteBySource(source: string): Promise<{
  success: boolean;
  deleted: {
    dailyMetrics: number;
    sleepRecords: number;
    heartRateRecords: number;
    workouts: number;
  };
}> {
  const response = await fetch(`${API_BASE}/source/${encodeURIComponent(source)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nell\'eliminazione');
  return response.json();
}

// ============================================
// EXPORT
// ============================================

export async function exportData(params?: DateRangeParams): Promise<{
  success: boolean;
  exportDate: string;
  data: {
    dailyMetrics: DailyMetric[];
    sleepData: SleepData[];
    heartRate: HeartRateData[];
    workouts: Workout[];
  };
}> {
  const searchParams = new URLSearchParams();
  if (params?.startDate) searchParams.set('startDate', params.startDate);
  if (params?.endDate) searchParams.set('endDate', params.endDate);
  if (params?.source) searchParams.set('source', params.source);

  const query = searchParams.toString();
  const response = await fetch(`${API_BASE}/export${query ? `?${query}` : ''}`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nell\'export');
  return response.json();
}

// ============================================
// GOALS
// ============================================

export async function getGoals(): Promise<{ success: boolean; goals: HealthGoal[] }> {
  const response = await fetch(`${API_BASE}/goals`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nel caricamento degli obiettivi');
  return response.json();
}

export async function createGoal(data: {
  goalType: string;
  title: string;
  description?: string;
  targetValue: number;
  unit: string;
  targetDate?: string;
}): Promise<{ success: boolean; goal: HealthGoal }> {
  const response = await fetch(`${API_BASE}/goals`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Errore nella creazione');
  return response.json();
}

export async function updateGoal(
  id: number,
  data: Partial<{
    goalType: string;
    title: string;
    description: string;
    targetValue: number;
    unit: string;
    targetDate: string;
    currentValue: number;
    isAchieved: boolean;
  }>
): Promise<{ success: boolean; goal: HealthGoal }> {
  const response = await fetch(`${API_BASE}/goals/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Errore nell\'aggiornamento');
  return response.json();
}

export async function deleteGoal(id: number): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/goals/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nell\'eliminazione');
  return response.json();
}

// ============================================
// OURA RING INTEGRATION
// ============================================

export async function connectOura(accessToken: string): Promise<{ success: boolean; email?: string }> {
  const response = await fetch(`${API_BASE}/oura/connect`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ accessToken }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Errore nella connessione');
  }
  return response.json();
}

export async function disconnectOura(): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/oura/disconnect`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nella disconnessione');
  return response.json();
}

export async function getOuraStatus(): Promise<{ success: boolean } & OuraStatus> {
  const response = await fetch(`${API_BASE}/oura/status`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nel caricamento dello stato');
  return response.json();
}

export async function syncOura(startDate: string, endDate: string): Promise<{
  success: boolean;
  imported: {
    sleepRecords: number;
    dailyMetrics: number;
    heartRateRecords: number;
    workouts: number;
  };
  errors: string[];
}> {
  const response = await fetch(`${API_BASE}/oura/sync`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ startDate, endDate }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Errore nella sincronizzazione');
  }
  return response.json();
}

// ============================================
// STRAVA INTEGRATION
// ============================================

export async function getStravaAuthUrl(): Promise<{ success: boolean; authUrl: string }> {
  const response = await fetch(`${API_BASE}/strava/auth-url`, {
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Strava non configurato');
  }
  return response.json();
}

export async function handleStravaCallback(code: string): Promise<{
  success: boolean;
  athlete?: { id: number; firstname: string; lastname: string; profile: string };
}> {
  const response = await fetch(`${API_BASE}/strava/callback`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Errore nell\'autenticazione');
  }
  return response.json();
}

export async function disconnectStrava(): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/strava/disconnect`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nella disconnessione');
  return response.json();
}

export async function getStravaStatus(): Promise<{ success: boolean } & StravaStatus> {
  const response = await fetch(`${API_BASE}/strava/status`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nel caricamento dello stato');
  return response.json();
}

export async function syncStrava(startDate: string, endDate: string): Promise<{
  success: boolean;
  imported: {
    workouts: number;
    dailyMetrics: number;
  };
  errors: string[];
}> {
  const response = await fetch(`${API_BASE}/strava/sync`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ startDate, endDate }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Errore nella sincronizzazione');
  }
  return response.json();
}

// ============================================
// GARMIN CONNECT INTEGRATION
// ============================================

export async function connectGarmin(username: string, password: string): Promise<{
  success: boolean;
  displayName?: string;
}> {
  const response = await fetch(`${API_BASE}/garmin/connect`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Errore nella connessione');
  }
  return response.json();
}

export async function disconnectGarmin(): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/garmin/disconnect`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nella disconnessione');
  return response.json();
}

export async function getGarminStatus(): Promise<{ success: boolean } & GarminStatus> {
  const response = await fetch(`${API_BASE}/garmin/status`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nel caricamento dello stato');
  return response.json();
}

export async function syncGarmin(startDate: string, endDate: string): Promise<{
  success: boolean;
  imported: {
    sleepRecords: number;
    dailyMetrics: number;
    heartRateRecords: number;
    workouts: number;
    bodyBattery: number;
    stress: number;
    respiration: number;
    fitnessAge: number;
  };
  fitnessAge: number | null;
  errors: string[];
}> {
  const response = await fetch(`${API_BASE}/garmin/sync`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ startDate, endDate }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Errore nella sincronizzazione');
  }
  return response.json();
}

// ============================================
// SOURCES
// ============================================

export async function getSources(): Promise<{ success: boolean; sources: string[] }> {
  const response = await fetch(`${API_BASE}/sources`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Errore nel caricamento delle sorgenti');
  return response.json();
}

// ============================================
// UTILITIES
// ============================================

// Format sleep duration from minutes to hours and minutes
export function formatSleepDuration(minutes: number | null): string {
  if (!minutes) return '-';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
}

// Format distance in km
export function formatDistance(km: number | null): string {
  if (!km) return '-';
  return `${km.toFixed(2)} km`;
}

// Get source label
export function getSourceLabel(source: string): string {
  const labels: Record<string, string> = {
    manual: 'Inserimento Manuale',
    oura: 'Oura Ring',
    strava: 'Strava',
    apple_health: 'Apple Salute',
    google_fit: 'Google Fit',
    garmin: 'Garmin',
    fitbit: 'Fitbit',
    samsung_health: 'Samsung Health',
    csv: 'Import CSV',
  };
  return labels[source] || source;
}

// Get source color
export function getSourceColor(source: string): { bg: string; text: string; hex: string } {
  const colors: Record<string, { bg: string; text: string; hex: string }> = {
    manual: { bg: 'bg-gray-100', text: 'text-gray-700', hex: '#6B7280' },
    oura: { bg: 'bg-purple-100', text: 'text-purple-700', hex: '#7C3AED' },
    strava: { bg: 'bg-orange-100', text: 'text-orange-700', hex: '#EA580C' },
    apple_health: { bg: 'bg-red-100', text: 'text-red-700', hex: '#DC2626' },
    google_fit: { bg: 'bg-green-100', text: 'text-green-700', hex: '#16A34A' },
    garmin: { bg: 'bg-emerald-100', text: 'text-emerald-700', hex: '#22C55E' },
    fitbit: { bg: 'bg-teal-100', text: 'text-teal-700', hex: '#0D9488' },
    samsung_health: { bg: 'bg-indigo-100', text: 'text-indigo-700', hex: '#4F46E5' },
    csv: { bg: 'bg-yellow-100', text: 'text-yellow-700', hex: '#CA8A04' },
  };
  return colors[source] || { bg: 'bg-gray-100', text: 'text-gray-700', hex: '#6B7280' };
}

// Format date to Italian format
export function formatDateItalian(dateString: string | null): string {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('it-IT', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// Get workout type label
export function getWorkoutTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    Run: 'Corsa',
    Ride: 'Ciclismo',
    Swim: 'Nuoto',
    Walk: 'Camminata',
    Hike: 'Escursione',
    Yoga: 'Yoga',
    WeightTraining: 'Pesi',
    Workout: 'Allenamento',
    Crossfit: 'Crossfit',
  };
  return labels[type] || type;
}

// Workout type options for manual entry
export const workoutTypes = [
  { value: 'Corsa', label: 'Corsa' },
  { value: 'Ciclismo', label: 'Ciclismo' },
  { value: 'Nuoto', label: 'Nuoto' },
  { value: 'Camminata', label: 'Camminata' },
  { value: 'Escursione', label: 'Escursione' },
  { value: 'Yoga', label: 'Yoga' },
  { value: 'Pesi', label: 'Pesi' },
  { value: 'Crossfit', label: 'Crossfit' },
  { value: 'Pilates', label: 'Pilates' },
  { value: 'Tennis', label: 'Tennis' },
  { value: 'Calcio', label: 'Calcio' },
  { value: 'Basket', label: 'Basket' },
  { value: 'Sci', label: 'Sci' },
  { value: 'Altro', label: 'Altro' },
];

// Goal type options
export const goalTypes = [
  { value: 'steps', label: 'Passi giornalieri', unit: 'passi', icon: 'footprints' },
  { value: 'sleep', label: 'Ore di sonno', unit: 'ore', icon: 'moon' },
  { value: 'workout_minutes', label: 'Minuti di allenamento settimanali', unit: 'min', icon: 'dumbbell' },
  { value: 'weight', label: 'Peso obiettivo', unit: 'kg', icon: 'scale' },
  { value: 'distance', label: 'Distanza settimanale', unit: 'km', icon: 'route' },
  { value: 'active_calories', label: 'Calorie attive giornaliere', unit: 'kcal', icon: 'flame' },
];

// Calculate daily goal progress
export function calculateDailyGoalProgress(goal: HealthGoal, dailyMetrics: DailyMetric[]): number {
  if (dailyMetrics.length === 0) return 0;

  const todayStr = new Date().toISOString().split('T')[0];
  const today = dailyMetrics.find(m => m.metricDate.split('T')[0] === todayStr);

  if (!today) return 0;

  switch (goal.goalType) {
    case 'steps':
      return ((today.steps || 0) / goal.targetValue) * 100;
    case 'active_calories':
      return ((today.caloriesActive || 0) / goal.targetValue) * 100;
    case 'distance':
      return ((today.distance || 0) / goal.targetValue) * 100;
    default:
      return goal.progressPercentage;
  }
}
